import { AssetRiskFactor, RiskFactorStats } from "~/operations";
import { Flex } from "~/components/Flex";
import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { RiskFactorsIndicators } from "./types";
import {
  getRiskFactorColors,
  getRiskFactorIcon,
} from "~/pages/space/security/components/RiskFactors/utils";
import { useColorMode } from "~/providers/color-mode";

export type RiskFactor =
  | Pick<
      RiskFactorStats | AssetRiskFactor,
      "mrn" | "title" | "indicator" | "affected" | "total" | "isPositive"
    >
  | null
  | undefined;

type RiskFactors = RiskFactor[] | null | undefined;

export type RiskFactorsIconsProps = {
  riskFactors: RiskFactors;
  shouldLimit?: boolean;
  activeOnly?: boolean; // this is used to filter out any risk factors that are inactive.  Inactive risk factors don't have an impact on the asset and are not displayed so they do not cause confusion to the end user.
  showBackgroundColor?: boolean; // we currently only show the background color when the risk factors are displayed in the header
  printView?: boolean;
  noWrap?: boolean;
  isActive?: boolean;
};

export const RiskFactorsIcons = ({
  riskFactors,
  shouldLimit = true,
  activeOnly = true,
  showBackgroundColor = false,
  printView = false,
  noWrap = false,
  isActive = true,
}: RiskFactorsIconsProps) => {
  const theme = useTheme();
  const { mode } = useColorMode();
  let activeRiskFactors = riskFactors || [];
  if (activeOnly) {
    // filter out the risk factors that are inactive, i.e. do not affect the asset
    activeRiskFactors =
      riskFactors?.flatMap((rf) => (rf && rf.affected > 0 ? rf : [])) || [];
  }

  const riskFactorsGroups = activeRiskFactors.reduce<
    Array<RiskFactorsIndicators>
  >((acc, item) => {
    if (!acc.includes(item?.indicator as RiskFactorsIndicators))
      acc.push(item?.indicator as RiskFactorsIndicators);

    return acc;
  }, []);

  const sortOrder = [
    "high-priority",
    "internet-exposed",
    "internet-facing",
    "exploitable",
    "db-access",
    "user-risk",
    "asset-in-use",
    "patch-available",
    "code-execution",
    "sensitive-data",
    "port",
    "package",
    "credentials",
    "containers",
    "eol",
    "defensive",
    "low-priority",
  ];

  const iconsToDisplay = sortOrder.flatMap(
    (rfi) => activeRiskFactors.find((arf) => arf?.indicator === rfi) ?? [],
  );

  const limitedIconsToDisplay = shouldLimit
    ? iconsToDisplay.slice(0, 4)
    : iconsToDisplay;

  const restRiskFactors =
    Object.keys(riskFactorsGroups).length - limitedIconsToDisplay.length || 0;

  return (
    <Flex
      display="inline-flex"
      flexWrap={noWrap ? "nowrap" : "wrap"}
      columnGap={showBackgroundColor ? 1 : 0}
    >
      {limitedIconsToDisplay.map((item) => {
        const { textColor, fillColor } = getRiskFactorColors({
          theme,
          light: mode === "light" || printView,
          isPositive: item.isPositive,
          isActive,
        });
        const icon = getRiskFactorIcon({
          indicator: item.indicator,
          sx: {
            color: textColor,
            mx: 0.25,
          },
        });

        const content = activeRiskFactors
          .filter((rf) => rf?.indicator === item.indicator)
          .map((rf) => <Typography key={rf?.mrn}>{rf?.title}</Typography>);

        return (
          <Tooltip
            title={content}
            key={item.indicator}
            placement="top"
            arrow
            data-testid="risk-factors-tooltip"
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                ...(showBackgroundColor && {
                  backgroundColor: fillColor,
                }),
                width: 32,
                height: 32,
                borderRadius: "50%",
              }}
            >
              {icon}
            </Box>
          </Tooltip>
        );
      })}
      {(riskFactors || []).length > 0 && restRiskFactors > 0 && shouldLimit && (
        <Typography
          variant="caption"
          color={theme.palette.text.warning}
          sx={{ alignSelf: "center" }}
          data-testid="risk-factors-rest-count"
        >
          +{restRiskFactors}
        </Typography>
      )}
    </Flex>
  );
};
